import type { ExecutionGate } from './gates/ExecutionGate';
import { GateWaitQueue } from './gates/GateWaitQueue';

const DEFAULT_RATE_LIMIT_MS = 30_000;
const DEFAULT_JITTER_MS = 5_000;
const LOG_THROTTLE_MS = 10_000;
const MIN_RATE_LIMIT_MS = 2_000;

export interface RateLimitActivationResult {
  limitedUntil: number;
  remainingMs: number;
  extended: boolean;
}

/**
 * Keeps global information about HTTP 429 rate limiting.
 *
 * Every authenticated request should check `isLimited()` before hitting the API.
 * When a 429 is received call `activate()` with Retry-After (if available).
 */
export class RateLimitManager implements ExecutionGate {
  private limitedUntil = 0;
  private lastLogAt = 0;
  private waitTimer?: ReturnType<typeof setTimeout>;
  private readonly waitQueue = new GateWaitQueue();

  isLimited(): boolean {
    return Date.now() < this.limitedUntil;
  }

  isBlocked(): boolean {
    return this.isLimited();
  }

  remainingMs(): number {
    return Math.max(0, this.limitedUntil - Date.now());
  }

  activate(
    retryAfterSeconds?: number,
    fallbackMs: number = DEFAULT_RATE_LIMIT_MS
  ): RateLimitActivationResult {
    const retryAfterMs =
      retryAfterSeconds != null && Number.isFinite(retryAfterSeconds)
        ? Math.max(0, retryAfterSeconds * 1000)
        : undefined;

    // Respect Retry-After if provided; enforce a floor to avoid zero-length gates.
    const duration =
      retryAfterMs != null
        ? Math.max(MIN_RATE_LIMIT_MS, retryAfterMs)
        : fallbackMs + Math.floor(Math.random() * DEFAULT_JITTER_MS);

    const until = Date.now() + duration;

    // Never shorten an existing rate limit.
    if (until <= this.limitedUntil) {
      return {
        limitedUntil: this.limitedUntil,
        remainingMs: this.remainingMs(),
        extended: false,
      };
    }

    this.limitedUntil = until;
    this.scheduleWakeup();

    return {
      limitedUntil: until,
      remainingMs: this.remainingMs(),
      extended: true,
    };
  }

  async waitUntilOpen(timeoutMs?: number): Promise<boolean> {
    if (!this.isLimited()) {
      return true;
    }

    this.scheduleWakeup();

    if (timeoutMs === undefined) {
      await this.waitQueue.waitUntilOpen(() => !this.isLimited());
      return true;
    }

    return this.waitQueue.waitUntilOpen(() => !this.isLimited(), timeoutMs);
  }

  async waitUntilClear(): Promise<void> {
    await this.waitUntilOpen();
  }

  shouldLog(throttleMs: number = LOG_THROTTLE_MS): boolean {
    const now = Date.now();

    if (now - this.lastLogAt < throttleMs) {
      return false;
    }

    this.lastLogAt = now;
    return true;
  }

  reset(): void {
    this.limitedUntil = 0;

    if (this.waitTimer) {
      clearTimeout(this.waitTimer);
      this.waitTimer = undefined;
    }

    this.waitQueue.notifyAll();
    this.waitQueue.clear();
    this.lastLogAt = 0;
  }

  private scheduleWakeup(): void {
    if (this.waitTimer) {
      clearTimeout(this.waitTimer);
    }

    this.waitTimer = setTimeout(() => {
      this.limitedUntil = 0;
      this.waitTimer = undefined;
      this.waitQueue.notifyAll();
    }, this.remainingMs());
  }
}

export const rateLimitManager = new RateLimitManager();

export function parseRetryAfterSeconds(
  headerValue: string | null
): number | undefined {
  if (!headerValue) {
    return undefined;
  }

  const seconds = Number(headerValue);

  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds;
  }

  const date = Date.parse(headerValue);

  if (!Number.isNaN(date)) {
    return Math.max(0, Math.ceil((date - Date.now()) / 1000));
  }

  return undefined;
}
